import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Zoom,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAllWorkTypes,
  createWorkType,
  updateWorkType,
  deleteWorkType,
} from "../../../Services/Services";

const WORK_COLORS = [
  "#0ea5e9",
  "#2563eb",
  "#4f46e5",
  "#7c3aed",
  "#a855f7",
  "#ec4899",
  "#f43f5e",
  "#10b981",
  "#f59e0b",
  "#06b6d4",
];

const PHASE_COLORS = [
  "#0ea5e9", "#2563eb", "#4f46e5", "#7c3aed",
  "#a855f7", "#ec4899", "#f43f5e", "#10b981",
];

const WorkTypeManagement = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add/Edit Modal
  const [openFormModal, setOpenFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editWorkTypeId, setEditWorkTypeId] = useState(null);
  const [workName, setWorkName] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [phases, setPhases] = useState([{ name: "", order_no: 1 }]);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // View Modal
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewWorkType, setViewWorkType] = useState(null);

  useEffect(() => {
    fetchWorkTypes();
  }, []);

  // ─── API ───

  const fetchWorkTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllWorkTypes();
      if (response.success) {
        const mapped = response.data.map((wt) => ({
          id: wt.id,
          name: wt.name,
          description: wt.description || "",
          phase_count: wt.phase_count || 0,
          phases: (wt.phases || [])
            .map((p) => ({
              id: p.id,
              name: p.name,
              order_no: Number(p.order_no),
            }))
            .sort((a, b) => a.order_no - b.order_no),
        }));
        setWorkTypes(mapped);
      } else {
        throw new Error(response.message || "Failed to fetch work types");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─── FORM HELPERS ───

  const resetForm = () => {
    setWorkName("");
    setWorkDescription("");
    setPhases([{ name: "", order_no: 1 }]);
    setErrors({});
    setIsEditMode(false);
    setEditWorkTypeId(null);
  };

  const openAddModal = () => {
    resetForm();
    setOpenFormModal(true);
  };

const openEditModal = (wt) => {
    setIsEditMode(true);
    setEditWorkTypeId(wt.id);
    setWorkName(wt.name);
    setWorkDescription(wt.description || "");
    setPhases(
      wt.phases.length > 0
        ? wt.phases.map((p) => ({
            id: p.id, 
            name: p.name,
            order_no: p.order_no,
          }))
        : [{ name: "", order_no: 1 }]
    );
    setErrors({});
    setOpenFormModal(true);
  };

  const handleViewWorkType = (wt) => {
    setViewWorkType(wt);
    setOpenViewModal(true);
  };

 const addPhaseRow = () => {
    const nextOrder =
      phases.length > 0 ? Math.max(...phases.map((p) => p.order_no)) + 1 : 1;
    setPhases([...phases, { name: "", order_no: nextOrder }]); 
  };

  const removePhaseRow = (index) => {
    if (phases.length <= 1) {
      toast.warning("At least one phase is required");
      return;
    }
    setPhases(phases.filter((_, i) => i !== index));
  };

  const updatePhaseRow = (index, field, value) => {
    const updated = [...phases];
    updated[index] = {
      ...updated[index],
      [field]: field === "order_no" ? Number(value) || "" : value,
    };
    setPhases(updated);
  };

  // ─── VALIDATE & SUBMIT ───

  const validateForm = () => {
    const errs = {};
    if (!workName.trim()) errs.workName = "Work type name is required";

    const validPhases = phases.filter((p) => p.name.trim());
    if (validPhases.length === 0) errs.phases = "At least one phase is required";

    phases.forEach((p, i) => {
      if (!p.name.trim()) errs[`phase_name_${i}`] = "Required";
      if (!p.order_no || p.order_no < 1) errs[`phase_order_${i}`] = "Min 1";
    });

    // Check duplicate order numbers
    const orders = phases.map((p) => p.order_no).filter(Boolean);
    if (new Set(orders).size !== orders.length) {
      errs.phases = "Phase order numbers must be unique";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      const payload = {
        name: workName.trim(),
        description: workDescription.trim(),
        phases: phases
          .filter((p) => p.name.trim())
          .map((p) => {
            const row = {
              name: p.name.trim(),
              order_no: parseInt(p.order_no, 10),
            };
            if (p.id) row.id = p.id;
            return row;
          }),
      };

      let response;
      if (isEditMode) {
        response = await updateWorkType(editWorkTypeId, payload);
      } else {
        response = await createWorkType(payload);
      }

      if (response.success) {
        await fetchWorkTypes();
        toast.success(
          isEditMode
            ? "Work type updated successfully!"
            : "Work type created successfully!"
        );
        setOpenFormModal(false);
        resetForm();
      } else {
        throw new Error(response.message || "Failed");
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        const rawErrors = err.response.data.errors;
        const flatErrors = {};

        // Flatten nested errors into simple strings
        Object.keys(rawErrors).forEach((key) => {
          const val = rawErrors[key];
          if (Array.isArray(val)) {
            // e.g. phases: [{name: ["Required"]}, ...]
            const messages = [];
            val.forEach((item, idx) => {
              if (typeof item === "object" && item !== null) {
                Object.values(item).forEach((msgs) => {
                  if (Array.isArray(msgs)) {
                    messages.push(`Phase ${idx + 1}: ${msgs[0]}`);
                  } else {
                    messages.push(`Phase ${idx + 1}: ${msgs}`);
                  }
                });
              } else {
                messages.push(String(item));
              }
            });
            flatErrors[key] = messages.join(", ");
          } else if (typeof val === "object" && val !== null) {
            flatErrors[key] = Object.values(val).flat().join(", ");
          } else if (Array.isArray(val)) {
            flatErrors[key] = val[0];
          } else {
            flatErrors[key] = String(val);
          }
        });

        setErrors(flatErrors);

        // Show first error in toast
        const firstMsg = Object.values(flatErrors)[0];
        toast.error(firstMsg || "Validation error");
      } else {
        toast.error(err.message || "Failed to save");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (wt) => {
    if (
      !window.confirm(
        `Delete "${wt.name}" and all its ${wt.phase_count} phases?`
      )
    )
      return;

    try {
      const response = await deleteWorkType(wt.id);
      if (response.success) {
        await fetchWorkTypes();
        toast.info("Work type deleted!");
      } else {
        throw new Error(response.message || "Failed");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  // ─── LOADING ───

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={44} thickness={4} color="primary" />
          <Typography
            sx={{ mt: 1.5, color: "text.secondary", fontSize: "0.85rem", fontWeight: 600 }}
          >
            Loading work types...
          </Typography>
        </Box>
      </Box>
    );
  }

  const HEADER_HEIGHT = 64;

  const dialogPaperSx = {
    m: 1.5,
    p: 0.5,
    borderRadius: "21px",
    bgcolor: "background.paper",
    backgroundImage: "none",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: (t) =>
      t.palette.mode === "dark"
        ? "0 25px 70px rgba(0,0,0,0.65)"
        : "0 25px 70px rgba(15,23,42,0.2)",
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage: (t) =>
          t.palette.mode === "dark"
            ? "radial-gradient(circle at 0% 0%, rgba(37,99,235,0.09), transparent 28%), radial-gradient(circle at 100% 0%, rgba(124,58,237,0.08), transparent 26%)"
            : "radial-gradient(circle at 0% 0%, rgba(37,99,235,0.08), transparent 25%), radial-gradient(circle at 100% 0%, rgba(124,58,237,0.07), transparent 24%)",
      }}
    >
      {/* ─── HEADER ─── */}
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          width: "100%",
          minHeight: HEADER_HEIGHT,
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? alpha(t.palette.background.paper, 0.88)
              : "rgba(255,255,255,0.9)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "0 2px 12px rgba(0,0,0,0.45)"
              : "0 2px 8px rgba(15,23,42,0.06)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1.2, md: 1.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: { xs: 0.7, sm: 1.4 },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 0.8,
              px: 1.4,
              py: 0.8,
              borderRadius: "11px",
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.06)
                  : "#f8fafc",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography sx={{ color: "primary.main", fontWeight: 800, fontSize: "1rem", lineHeight: 1 }}>
              {workTypes.length}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.72rem", fontWeight: 600 }}>
              work types
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            sx={{
              minHeight: 40,
              px: { xs: 1.4, sm: 2 },
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.76rem", sm: "0.85rem" },
              bgcolor: "#2563eb",
              color: "#fff",
              boxShadow: "0 5px 16px rgba(37,99,235,0.28)",
              "&:hover": { bgcolor: "#1d4ed8" },
            }}
          >
            Add Work Type
          </Button>
        </Box>
      </Box>

      {/* ─── CONTENT ─── */}
      <Box
        component="main"
        sx={{
          width: "100%",
          px: { xs: 1.5, sm: 2.5, md: 4 },
          py: { xs: 2.5, md: 3.5 },
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2.5, borderRadius: "13px" }}>
            {error}
          </Alert>
        )}

        {/* ── EMPTY STATE ── */}
        {workTypes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              maxWidth: 650,
              mx: "auto",
              mt: { xs: 5, md: 8 },
              p: { xs: 3, md: 5 },
              textAlign: "center",
              borderRadius: "24px",
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 68,
                height: 68,
                mx: "auto",
                mb: 2,
                display: "grid",
                placeItems: "center",
                borderRadius: "20px",
                color: "primary.main",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.primary.main, 0.15)
                    : "#eff6ff",
              }}
            >
              <WorkOutlineIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, mb: 0.8 }}>
              No work types created yet
            </Typography>
            <Typography sx={{ maxWidth: 430, mx: "auto", mb: 2.8, color: "text.secondary", fontSize: "0.88rem", lineHeight: 1.6 }}>
              Create different work types (New Work, Repair, Maintenance) each with their own workflow phases.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddModal}
              sx={{
                px: 2.8,
                py: 1.1,
                borderRadius: "11px",
                bgcolor: "#2563eb",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 6px 16px rgba(37,99,235,0.22)",
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Create First Work Type
            </Button>
          </Paper>
        ) : (
          /* ── WORK TYPE CARDS GRID ── */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 1.5, sm: 2, md: 2.5 },
            }}
          >
            {workTypes.map((wt, index) => {
              const color = WORK_COLORS[index % WORK_COLORS.length];
              return (
                <Zoom in key={wt.id} style={{ transitionDelay: `${index * 60}ms` }}>
                  <Paper
                    elevation={0}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      p: 2,
                      borderRadius: "16px",
                      bgcolor: "background.paper",
                      backgroundImage: "none",
                      border: "1px solid",
                      borderColor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(color, 0.3)
                          : `${color}35`,
                      borderTop: `5px solid ${color}`,
                      boxShadow: (t) =>
                        t.palette.mode === "dark"
                          ? "0 6px 18px rgba(0,0,0,0.35)"
                          : "0 6px 18px rgba(15,23,42,0.06)",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: color,
                        boxShadow: (t) =>
                          t.palette.mode === "dark"
                            ? `0 15px 30px -12px ${alpha(color, 0.55)}`
                            : `0 15px 30px -12px ${color}80`,
                      },
                    }}
                  >
                    {/* Top Row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "10px",
                            bgcolor: color,
                            color: "#fff",
                          }}
                        >
                          <WorkOutlineIcon sx={{ fontSize: "1.1rem" }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "text.primary", lineHeight: 1.2 }}>
                            {wt.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.65rem", color: "text.disabled", fontWeight: 600 }}>
                            {wt.phase_count} Phase{wt.phase_count !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: "flex", gap: 0.2, mt: -0.5, mr: -0.5 }}>
                        <Tooltip title="View Phases" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleViewWorkType(wt)}
                            sx={{
                              p: "5px",
                              color: "text.disabled",
                              "&:hover": { color, bgcolor: alpha(color, 0.14) },
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: "0.92rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(wt)}
                            sx={{
                              p: "5px",
                              color: "text.disabled",
                              "&:hover": { color, bgcolor: alpha(color, 0.14) },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: "0.92rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(wt)}
                            sx={{
                              p: "5px",
                              color: "text.disabled",
                              "&:hover": {
                                color: "error.main",
                                bgcolor: isDark ? alpha(theme.palette.error.main, 0.16) : "#fef2f2",
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: "0.92rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Description */}
                    {wt.description && (
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "text.secondary",
                          mb: 1.2,
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {wt.description}
                      </Typography>
                    )}

                    {/* Phase Preview Chips */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {wt.phases.slice(0, 4).map((p, pi) => (
                        <Chip
                          key={p.id || pi}
                          label={`${p.order_no}. ${p.name}`}
                          size="small"
                          sx={{
                            height: 22,
                            borderRadius: "6px",
                            bgcolor: alpha(color, isDark ? 0.18 : 0.08),
                            color,
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.7 },
                          }}
                        />
                      ))}
                      {wt.phases.length > 4 && (
                        <Chip
                          label={`+${wt.phases.length - 4} more`}
                          size="small"
                          sx={{
                            height: 22,
                            borderRadius: "6px",
                            bgcolor: "action.hover",
                            color: "text.secondary",
                            fontSize: "0.62rem",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Zoom>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ═══════════ ADD / EDIT MODAL ═══════════ */}
      <Dialog
        open={openFormModal}
        onClose={() => {
          if (!formLoading) {
            setOpenFormModal(false);
            resetForm();
          }
        }}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                borderRadius: "12px",
                color: isEditMode ? "#7c3aed" : "primary.main",
                bgcolor: isEditMode
                  ? isDark ? alpha("#7c3aed", 0.18) : "#f5f3ff"
                  : isDark ? alpha(theme.palette.primary.main, 0.18) : "#eff6ff",
              }}
            >
              {isEditMode ? <EditOutlinedIcon /> : <AddIcon />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.15rem", fontWeight: 800 }}>
                {isEditMode ? "Edit Work Type" : "Add New Work Type"}
              </Typography>
              <Typography sx={{ mt: 0.2, color: "text.secondary", fontSize: "0.72rem" }}>
                {isEditMode
                  ? "Update work type and its phases"
                  : "Define a work type with its workflow phases"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Work Name */}
            <TextField
              label="Work Type Name"
              value={workName}
              onChange={(e) => {
                setWorkName(e.target.value);
                if (e.target.value.trim())
                  setErrors((prev) => ({ ...prev, workName: undefined }));
              }}
              fullWidth
              required
              error={Boolean(errors.workName)}
              helperText={errors.workName || ""}
              size="small"
              autoFocus
              placeholder="e.g. New Work, Repair Work, Maintenance"
              disabled={formLoading}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "11px" },
                "& .MuiInputLabel-root": { fontWeight: 600 },
              }}
            />

            {/* Description (Optional) */}
            <TextField
              label="Description (Optional)"
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Brief description of this work type"
              disabled={formLoading}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "11px" },
                "& .MuiInputLabel-root": { fontWeight: 600 },
              }}
            />

            {/* Phases Section */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "text.primary" }}>
                  Workflow Phases *
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: "0.9rem" }} />}
                  onClick={addPhaseRow}
                  disabled={formLoading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    color: "primary.main",
                  }}
                >
                  Add Phase
                </Button>
              </Box>

             {errors.phases && (
                <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: "0.75rem" }}>
                  {typeof errors.phases === "string"
                    ? errors.phases
                    : "Please fix phase errors below"}
                </Alert>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
                {phases.map((phase, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "10px",
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.common.white, 0.04)
                          : "#f8fafc",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {/* Order */}
                    <TextField
                      label="Order"
                      type="number"
                      value={phase.order_no}
                      onChange={(e) => updatePhaseRow(index, "order_no", e.target.value)}
                      size="small"
                      sx={{
                        width: 75,
                        flexShrink: 0,
                        "& .MuiInputBase-root": { borderRadius: "8px", height: 36 },
                        "& .MuiInputLabel-root": { fontSize: "0.65rem" },
                        "& .MuiInputBase-input": { fontSize: "0.78rem", p: "6px 8px" },
                      }}
                      inputProps={{ min: 1 }}
                      disabled={formLoading}
                      error={Boolean(errors[`phase_order_${index}`])}
                    />

                    {/* Name */}
                    <TextField
                      label="Phase Name"
                      value={phase.name}
                      onChange={(e) => updatePhaseRow(index, "name", e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={`e.g. ${["Design", "Cutting", "Welding", "Assembly", "Testing", "Painting", "QC", "Dispatch"][index] || "Phase " + (index + 1)}`}
                      disabled={formLoading}
                      error={Boolean(errors[`phase_name_${index}`])}
                      helperText={errors[`phase_name_${index}`] || ""}
                      sx={{
                        "& .MuiInputBase-root": { borderRadius: "8px", height: 36 },
                        "& .MuiInputLabel-root": { fontSize: "0.65rem" },
                        "& .MuiInputBase-input": { fontSize: "0.78rem", p: "6px 10px" },
                      }}
                    />

                    {/* Remove */}
                    <IconButton
                      size="small"
                      onClick={() => removePhaseRow(index)}
                      disabled={formLoading || phases.length <= 1}
                      sx={{
                        color: "error.main",
                        p: "4px",
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor: isDark
                            ? alpha(theme.palette.error.main, 0.16)
                            : "#fef2f2",
                        },
                      }}
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: "1.1rem" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.3, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => {
              setOpenFormModal(false);
              resetForm();
            }}
            variant="outlined"
            disabled={formLoading}
            sx={{
              px: 2,
              borderRadius: "10px",
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            disabled={formLoading}
            sx={{
              px: 2.3,
              borderRadius: "10px",
              bgcolor: "#2563eb",
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: "#1d4ed8" },
            }}
          >
            {formLoading ? (
              <CircularProgress size={21} sx={{ color: "#fff" }} />
            ) : isEditMode ? (
              "Update Work Type"
            ) : (
              "Create Work Type"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ VIEW MODAL (Phases Roadmap) ═══════════ */}
      <Dialog
        open={openViewModal}
        onClose={() => {
          setOpenViewModal(false);
          setViewWorkType(null);
        }}
        fullWidth
        maxWidth="md"
        disableScrollLock
        PaperProps={{ sx: dialogPaperSx }}
      >
        {viewWorkType && (
          <>
            <DialogTitle sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "12px",
                    color: "#10b981",
                    bgcolor: isDark ? alpha("#10b981", 0.18) : "#ecfdf5",
                  }}
                >
                  <VisibilityOutlinedIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "1.15rem", fontWeight: 800 }}>
                    {viewWorkType.name}
                  </Typography>
                  <Typography sx={{ mt: 0.2, color: "text.secondary", fontSize: "0.72rem" }}>
                    {viewWorkType.phase_count} phases in workflow
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
              {viewWorkType.phases.length === 0 ? (
                <Typography sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
                  No phases defined for this work type.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 1, sm: 0.5 },
                    py: 2,
                  }}
                >
                  {viewWorkType.phases.map((phase, index) => {
                    const pColor = PHASE_COLORS[index % PHASE_COLORS.length];
                    const isLast = index === viewWorkType.phases.length - 1;
                    return (
                      <React.Fragment key={phase.id || index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: "12px",
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: alpha(pColor, 0.3),
                            borderTop: `4px solid ${pColor}`,
                            width: { xs: "100%", sm: 160 },
                            textAlign: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              mx: "auto",
                              mb: 0.8,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: "50%",
                              bgcolor: pColor,
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 800,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "text.primary" }}>
                            {phase.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.62rem", color: "text.disabled", mt: 0.3 }}>
                            Order #{phase.order_no}
                          </Typography>
                        </Paper>
                        {!isLast && (
                          <ArrowForwardIcon
                            sx={{
                              display: { xs: "none", sm: "block" },
                              fontSize: 24,
                              color: alpha(pColor, 0.5),
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, pb: 2 }}>
              <Button
                onClick={() => {
                  setOpenViewModal(false);
                  setViewWorkType(null);
                }}
                variant="outlined"
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </Box>
  );
};

export default WorkTypeManagement;